import "reactflow/dist/style.css";
import ReactFlow, {
  addEdge,
  useNodesState,
  useEdgesState,
  Background,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import dagre from "dagre";
import React, { useState, useEffect } from "react";
import Content from "../../layout/content/Content";
import { Card } from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../components/Component";
import purchaseRequestApis from "../../api/master/purchaseRequest";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Flex, Spin } from "antd";
import dayjs from "dayjs";
import Swal from "sweetalert2";

// Custom node component
const CustomNode = ({ data }: any) => {
  const navigate = useNavigate();
  return (
    <div
      style={{
        border: "2px solid #ddd",
        fontSize: "14px",
        padding: "12px",
        borderRadius: "8px",
        width: "280px",
        backgroundColor: "white",
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.15)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
      onClick={() => {
        if (data.label === "Purchase Request") navigate(`/view_pr/${data.id}`);
        if (data.label === "Quotation") navigate(`/view/${data.id}`);
        if (data.label === "Purchase Order")
          navigate(`/view-purchase-order/${data.id}`);
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: "#007acc",
          width: "10px",
          height: "10px",
          border: "2px solid white",
        }}
      />
      <div
        style={{
          backgroundColor: "rgba(0, 122, 204, 0.1)",
          color: "#007acc",
          padding: "8px",
          borderRadius: "6px",
          textAlign: "center",
          fontWeight: "600",
          marginBottom: "8px",
        }}
      >
        {data.label}
      </div>
      <div style={{ marginBottom: "4px", fontSize: "13px" }}>
        <strong>Sequence ID:</strong>{" "}
        <span style={{ color: "#666" }}>{data.trackingId}</span>
      </div>
      <div style={{ marginBottom: "4px", fontSize: "13px" }}>
        <strong>Created At:</strong>{" "}
        <span style={{ color: "#666" }}>
          {dayjs(data.createdAt).format("DD-MMM-YYYY")}
        </span>
      </div>
      <div style={{ fontSize: "13px" }}>
        <strong>Created By:</strong>{" "}
        <span style={{ color: "#666" }}>{data.createdBy}</span>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: "#007acc",
          width: "10px",
          height: "10px",
          border: "2px solid white",
        }}
      />
    </div>
  );
};

const nodeTypes = {
  selectorNode: CustomNode,
};

// Function to calculate layout
const dagreLayout = (nodes: any[], edges: any[]) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: "TB", // Top to bottom layout
    ranksep: 150, // Increased spacing between ranks
    nodesep: 100, // Increased spacing between nodes
    edgesep: 50, // Spacing between edges
    marginx: 50, // Horizontal margin
    marginy: 50, // Vertical margin
  });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((node) => g.setNode(node.id, { width: 280, height: 150 })); // Adjusted dimensions
  edges.forEach((edge) => g.setEdge(edge.source, edge.target));

  dagre.layout(g);

  return nodes.map((node) => {
    const nodeData = g.node(node.id);
    return {
      ...node,
      position: { x: nodeData.x - 140, y: nodeData.y - 75 }, // Center nodes with adjusted width
      type: "selectorNode",
    };
  });
};

// Custom Zoom Controls Component
const ZoomControls = () => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  return (
    <div
      style={{
        position: "absolute",
        top: "20px",
        right: "20px",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        backgroundColor: "white",
        padding: "10px",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        border: "1px solid #ddd",
      }}
    >
      <button
        onClick={() => zoomIn()}
        style={{
          width: "40px",
          height: "40px",
          border: "1px solid #ddd",
          backgroundColor: "white",
          borderRadius: "4px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          fontWeight: "bold",
          color: "#007acc",
        }}
        title="Zoom In"
      >
        +
      </button>
      <button
        onClick={() => zoomOut()}
        style={{
          width: "40px",
          height: "40px",
          border: "1px solid #ddd",
          backgroundColor: "white",
          borderRadius: "4px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          fontWeight: "bold",
          color: "#007acc",
        }}
        title="Zoom Out"
      >
        −
      </button>
      <button
        onClick={() => fitView({ padding: 0.2, includeHiddenNodes: true })}
        style={{
          width: "40px",
          height: "40px",
          border: "1px solid #ddd",
          backgroundColor: "white",
          borderRadius: "4px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "12px",
          fontWeight: "bold",
          color: "#007acc",
        }}
        title="Fit View"
      >
        ⌂
      </button>
    </div>
  );
};

const TrackPurchaseRequests = () => {
  let { prId }: any = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const locationState = useLocation().state;
  const { prNumber, indentNumber } = locationState || {};

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    getPRTracking();
  }, []);

  const getPRTracking = async () => {
    try {
      const { response, status, message } =
        await purchaseRequestApis.prTracking(prId);
      if (status) {
        setNodes(dagreLayout(response.initialNodes, response.initialEdges));
        setEdges(response.initialEdges);
      } else {
        Swal.fire("Error", message, "error");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching PR tracking data:", error);
    }
  };

  const onFlowInit = (instance: any) => {
    if (!isLoading && nodes.length > 0 && edges.length > 0) {
      // Delay to ensure nodes are rendered
      setTimeout(() => {
        instance.fitView({
          padding: 0.2,
          includeHiddenNodes: true,
          minZoom: 0.1,
          maxZoom: 1.2,
        });
      }, 100);
    }
  };

  const onConnect = (params: any) => setEdges((eds) => addEdge(params, eds));

  return (
    <>
      <Head title="Purchase Requests - Track" />
      <Content>
        <Card className="card-bordered">
          <div className="card-inner card-inner-lg">
            <BlockHead size="lg">
              <BlockBetween>
                <BlockHeadContent>
                  <BlockTitle tag="h4">Track Purchase Request </BlockTitle>
                  <BlockDes>
                    <p>PR # - {prNumber && prNumber}</p>
                  </BlockDes>
                </BlockHeadContent>
                <div className="d-flex">
                  <Button color="primary" onClick={() => navigate(-1)}>
                    <Icon name="arrow-left" />
                    <span>Back</span>
                  </Button>
                </div>
              </BlockBetween>
            </BlockHead>
            <Block>
              <Card
                className="card-bordered"
                style={{ height: "800px", overflow: "hidden" }}
              >
                {isLoading ? (
                  <Flex align="center" className="pageLoader">
                    <Spin size="large" className="m-auto" />
                  </Flex>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "800px",
                      position: "relative",
                    }}
                  >
                    <ReactFlowProvider>
                      <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        nodeTypes={nodeTypes}
                        onInit={onFlowInit}
                        minZoom={0.1}
                        maxZoom={2}
                        translateExtent={[
                          [-5000, -5000],
                          [5000, 5000],
                        ]}
                        nodeExtent={[
                          [-5000, -5000],
                          [5000, 5000],
                        ]}
                        zoomOnScroll={true}
                        zoomOnPinch={true}
                        panOnScroll={true}
                        preventScrolling={false}
                        style={{ width: "100%", height: "100%" }}
                        fitView={true}
                        fitViewOptions={{
                          padding: 0.2,
                          includeHiddenNodes: true,
                          minZoom: 0.1,
                          maxZoom: 1.2,
                        }}
                      >
                        <Background color="#f0f0f0" gap={20} size={1} />
                        <ZoomControls />
                      </ReactFlow>
                    </ReactFlowProvider>
                  </div>
                )}
              </Card>
            </Block>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default TrackPurchaseRequests;
